/**
 * Performance Monitor Component
 * Development tool for monitoring mobile performance metrics in real-time
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
// Mobile performance utilities - inline implementation
const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const isSlowConnection = () => 'connection' in navigator && (navigator as unknown as { connection?: { effectiveType?: string } }).connection?.effectiveType === '2g';
const getDeviceMemory = () => 'deviceMemory' in navigator ? (navigator as unknown as { deviceMemory?: number }).deviceMemory || 4 : 4;

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

interface DeviceInfo {
  isMobile: boolean;
  isSlowConnection: boolean;
  deviceMemory: number;
  viewportSize: string;
  pixelRatio: number;
  connectionType: string;
  userAgent: string;
}

const PerformanceMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in development
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    // Collect device information
    const info: DeviceInfo = {
      isMobile: isMobile(),
      isSlowConnection: isSlowConnection(),
      deviceMemory: getDeviceMemory(),
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio || 1,
      connectionType: (navigator as unknown as { connection?: { effectiveType?: string } }).connection?.effectiveType || 'unknown',
      userAgent: navigator.userAgent
    };
    setDeviceInfo(info);

    // Monitor performance metrics
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming;
          const loadTime = navEntry.loadEventEnd - navEntry.navigationStart;
          
          setMetrics(prev => [...prev, {
            name: 'Page Load Time',
            value: Math.round(loadTime),
            rating: loadTime < 2000 ? 'good' : loadTime < 4000 ? 'needs-improvement' : 'poor',
            timestamp: Date.now()
          }]);
        }
        
        if (entry.entryType === 'paint') {
          const paintEntry = entry as PerformancePaintTiming;
          setMetrics(prev => [...prev, {
            name: paintEntry.name === 'first-contentful-paint' ? 'FCP' : 'FP',
            value: Math.round(paintEntry.startTime),
            rating: paintEntry.startTime < 1800 ? 'good' : paintEntry.startTime < 3000 ? 'needs-improvement' : 'poor',
            timestamp: Date.now()
          }]);
        }
      });
    });

    observer.observe({ entryTypes: ['navigation', 'paint'] });

    // Monitor memory usage
    const memoryInterval = setInterval(() => {
      if ('memory' in performance) {
        const memory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
        setMetrics(prev => [...prev.slice(-10), {
          name: 'JS Heap Used',
          value: Math.round((memory?.usedJSHeapSize || 0) / 1024 / 1024),
          rating: (memory?.usedJSHeapSize || 0) < 50 * 1024 * 1024 ? 'good' :
                  (memory?.usedJSHeapSize || 0) < 100 * 1024 * 1024 ? 'needs-improvement' : 'poor',
          timestamp: Date.now()
        }]);
      }
    }, 5000);

    return () => {
      observer.disconnect();
      clearInterval(memoryInterval);
    };
  }, []);

  // Don't render in production
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'good': return 'bg-green-500';
      case 'needs-improvement': return 'bg-yellow-500';
      case 'poor': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getMetricUnit = (name: string) => {
    if (name.includes('Time') || name === 'FCP' || name === 'FP') return 'ms';
    if (name.includes('Heap')) return 'MB';
    return '';
  };

  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsVisible(true)}
          variant="outline"
          size="sm"
          className="bg-background/80 backdrop-blur-sm"
        >
          📊 Perf Monitor
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-h-96 overflow-y-auto">
      <Card className="bg-background/95 backdrop-blur-sm border shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Performance Monitor</CardTitle>
            <Button
              onClick={() => setIsVisible(false)}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
            >
              ✕
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          {/* Device Information */}
          {deviceInfo && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground">Device Info</h4>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div>Mobile: {deviceInfo.isMobile ? '✅' : '❌'}</div>
                <div>Slow Conn: {deviceInfo.isSlowConnection ? '🐌' : '⚡'}</div>
                <div>Memory: {deviceInfo.deviceMemory}GB</div>
                <div>Viewport: {deviceInfo.viewportSize}</div>
                <div>Pixel Ratio: {deviceInfo.pixelRatio}x</div>
                <div>Connection: {deviceInfo.connectionType}</div>
              </div>
            </div>
          )}

          {/* Performance Metrics */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground">Metrics</h4>
            <div className="space-y-1">
              {metrics.slice(-6).map((metric, index) => (
                <div key={`${metric.name}-${index}`} className="flex items-center justify-between text-xs">
                  <span className="truncate">{metric.name}</span>
                  <div className="flex items-center gap-1">
                    <span className="font-mono">
                      {metric.value}{getMetricUnit(metric.name)}
                    </span>
                    <Badge 
                      className={`h-4 w-4 p-0 ${getRatingColor(metric.rating)}`}
                      title={metric.rating}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground">Actions</h4>
            <div className="flex gap-1">
              <Button
                onClick={() => setMetrics([])}
                variant="outline"
                size="sm"
                className="h-6 text-xs"
              >
                Clear
              </Button>
              <Button
                onClick={() => {
                  const data = {
                    deviceInfo,
                    metrics: metrics.slice(-10),
                    timestamp: new Date().toISOString()
                  };
                  console.log('Performance Report:', data);
                  navigator.clipboard?.writeText(JSON.stringify(data, null, 2));
                }}
                variant="outline"
                size="sm"
                className="h-6 text-xs"
              >
                Export
              </Button>
            </div>
          </div>

          {/* Performance Tips */}
          {deviceInfo?.isMobile && (
            <div className="space-y-1">
              <h4 className="text-xs font-semibold text-muted-foreground">Mobile Tips</h4>
              <div className="text-xs text-muted-foreground space-y-1">
                {deviceInfo.isSlowConnection && (
                  <div className="text-yellow-600">⚠️ Slow connection detected</div>
                )}
                {deviceInfo.deviceMemory < 4 && (
                  <div className="text-orange-600">⚠️ Low memory device</div>
                )}
                {metrics.some(m => m.name.includes('Time') && m.rating === 'poor') && (
                  <div className="text-red-600">🐌 Poor loading performance</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceMonitor;
